/**
 * Downscale and re-encode a photo before it is uploaded.
 *
 * There is no client-side compression anywhere else in this codebase, and the
 * image path has no size guard at all — only the document library does. That is
 * survivable when photos arrive from a desktop dropzone. It is not survivable
 * in the field: `<input capture>` hands back **3-12 MB per photo**, a rep takes
 * dozens at a site, and the upload is happening on LTE in a car park. Twenty
 * full-resolution photos is slow, drains the battery, and can simply fail.
 *
 * The second reason is HEIC. iPhones can return `image/heic`, which some
 * browsers will not render in an `<img>` at all — so a photo uploaded raw can
 * be invisible in the very gallery it was taken for. Re-encoding to JPEG fixes
 * that as a side effect of the resize.
 *
 * **This never throws.** A photo that cannot be compressed is uploaded as it
 * came off the camera; losing the picture because the canvas failed would be a
 * far worse outcome than an 8 MB upload.
 */

export interface CompressImageOptions {
	/** Longest edge, in pixels. 2048 is generous for reading a pylon sign. */
	maxDimension?: number
	/** JPEG quality. 0.8 is the usual knee — below it, signage text goes soft. */
	quality?: number
	/** Files already smaller than this are left alone. */
	skipUnderBytes?: number
}

export interface CompressImageResult {
	/** The file to upload. The original when nothing could be improved. */
	file: File
	originalBytes: number
	bytes: number
	/** False when the original is being returned unchanged. */
	compressed: boolean
	/** Why it was left alone, for logging and for the tests. */
	reason?: 'not-an-image' | 'already-small' | 'decode-failed' | 'encode-failed' | 'no-gain'
}

const DEFAULTS = {
	maxDimension: 2048,
	quality: 0.8,
	skipUnderBytes: 512 * 1024,
}

/**
 * Formats a browser will render directly in an `<img>`. HEIC is deliberately
 * absent: it is the format worth re-encoding even when the file is small.
 */
const WEB_SAFE = /^image\/(jpeg|png|webp|gif)$/i

const unchanged = (file: File, reason: CompressImageResult['reason']): CompressImageResult => ({
	file,
	originalBytes: file.size,
	bytes: file.size,
	compressed: false,
	reason,
})

/**
 * Decode to something drawable.
 *
 * `createImageBitmap` with `imageOrientation: 'from-image'` is the important
 * path: a phone stores a portrait photo as landscape pixels plus an EXIF
 * rotation flag, and drawing it to a canvas without honouring that flag saves
 * every portrait photo on its side. The `<img>` fallback applies EXIF
 * orientation itself in current browsers, which is why it is a usable second
 * choice rather than a trap.
 */
const decode = async (file: File): Promise<ImageBitmap | HTMLImageElement | null> => {
	if (typeof createImageBitmap === 'function') {
		try {
			// Cast because this TypeScript's lib.dom still types `imageOrientation`
			// as "none" | "flipY", predating the spec's "from-image" — which is
			// the value browsers actually implement and the only one that applies
			// the EXIF rotation.
			return await createImageBitmap(file, {
				imageOrientation: 'from-image',
			} as unknown as ImageBitmapOptions)
		} catch {
			/* Fall through — Safari has historically rejected the option bag. */
		}
		try {
			return await createImageBitmap(file)
		} catch {
			/* An undecodable format, e.g. HEIC off iOS in a non-Apple browser. */
		}
	}

	if (typeof Image !== 'function' || typeof URL?.createObjectURL !== 'function') return null

	const url = URL.createObjectURL(file)
	try {
		return await new Promise<HTMLImageElement | null>(resolve => {
			const img = new Image()
			img.onload = () => resolve(img)
			img.onerror = () => resolve(null)
			img.src = url
		})
	} finally {
		URL.revokeObjectURL(url)
	}
}

const sizeOf = (source: ImageBitmap | HTMLImageElement): { width: number; height: number } => ({
	width: (source as HTMLImageElement).naturalWidth || source.width,
	height: (source as HTMLImageElement).naturalHeight || source.height,
})

const toBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> =>
	new Promise(resolve => {
		try {
			canvas.toBlob(blob => resolve(blob), 'image/jpeg', quality)
		} catch {
			resolve(null)
		}
	})

/** Swap the extension, so the uploaded name matches what it now is. */
const jpegName = (name: string): string =>
	`${(name || 'photo').replace(/\.[^.]+$/, '') || 'photo'}.jpg`

export const compressImage = async (
	file: File,
	options: CompressImageOptions = {},
): Promise<CompressImageResult> => {
	const { maxDimension, quality, skipUnderBytes } = { ...DEFAULTS, ...options }

	if (!file?.type?.startsWith('image/')) return unchanged(file, 'not-an-image')

	// A small file that a browser will already render is not worth the cycles
	// or the generation loss. A small HEIC still is — it may not render at all.
	if (file.size <= skipUnderBytes && WEB_SAFE.test(file.type)) {
		return unchanged(file, 'already-small')
	}

	const source = await decode(file)
	if (!source) return unchanged(file, 'decode-failed')

	try {
		const { width, height } = sizeOf(source)
		if (!width || !height) return unchanged(file, 'decode-failed')

		// Never upscale: a 900px photo enlarged to 2048 is bigger, not better.
		const scale = Math.min(1, maxDimension / Math.max(width, height))
		const targetWidth = Math.max(1, Math.round(width * scale))
		const targetHeight = Math.max(1, Math.round(height * scale))

		const canvas = document.createElement('canvas')
		canvas.width = targetWidth
		canvas.height = targetHeight

		const context = canvas.getContext('2d')
		if (!context) return unchanged(file, 'encode-failed')
		context.drawImage(source as CanvasImageSource, 0, 0, targetWidth, targetHeight)

		const blob = await toBlob(canvas, quality)
		if (!blob || !blob.size) return unchanged(file, 'encode-failed')

		// Re-encoding an already-efficient JPEG can make it larger. Keeping the
		// original is both smaller and lossless; the exception is a format the
		// browser may not render, where the conversion is the point.
		if (blob.size >= file.size && WEB_SAFE.test(file.type)) {
			return unchanged(file, 'no-gain')
		}

		return {
			file: new File([blob], jpegName(file.name), {
				type: 'image/jpeg',
				lastModified: file.lastModified || Date.now(),
			}),
			originalBytes: file.size,
			bytes: blob.size,
			compressed: true,
		}
	} catch {
		return unchanged(file, 'encode-failed')
	} finally {
		// Bitmaps hold decoded pixels — several full-resolution photos' worth is
		// enough to be killed on a phone.
		if (typeof (source as ImageBitmap).close === 'function') (source as ImageBitmap).close()
	}
}

export default compressImage
